import { useState, useEffect } from "react";
import { Sparkles, Loader2, Copy, Check, ChevronDown, ChevronUp, Wand2, Save, FileText, Trash2, FolderOpen } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Options for dropdowns
const PROPERTY_TYPES = [
  { value: "apartament", label: "Apartament" },
  { value: "garsoniera", label: "Garsonieră" },
  { value: "casa", label: "Casă" },
  { value: "vila", label: "Vilă" },
  { value: "penthouse", label: "Penthouse" },
  { value: "duplex", label: "Duplex" },
  { value: "studio", label: "Studio" },
];

const COMPARTMENTS = [
  { value: "decomandat", label: "Decomandat" },
  { value: "semidecomandat", label: "Semidecomandat" },
  { value: "open_space", label: "Open Space" },
  { value: "circular", label: "Circular" },
];

const PROPERTY_CONDITIONS = [
  { value: "renovat_recent", label: "Renovat recent" },
  { value: "renovat_5ani", label: "Renovat în ultimii 5 ani" },
  { value: "stare_buna", label: "Stare bună" },
  { value: "nerenovat", label: "Nerenovat" },
  { value: "necesita_renovare", label: "Necesită renovare" },
];

const FLOOR_TYPES = [
  { value: "parchet", label: "Parchet" },
  { value: "parchet_laminat", label: "Parchet laminat" },
  { value: "parchet_masiv", label: "Parchet masiv" },
  { value: "gresie", label: "Gresie" },
  { value: "marmura", label: "Marmură" },
];

const HEATING_TYPES = [
  { value: "centrala_proprie", label: "Centrală proprie" },
  { value: "termoficare", label: "Termoficare" },
  { value: "centrala_bloc", label: "Centrală de bloc" },
  { value: "pardoseala", label: "Încălzire în pardoseală" },
];

const AIListingGenerator = ({ onTitleGenerated, onDescriptionGenerated, initialData = {} }) => {
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [generatedTitle, setGeneratedTitle] = useState("");
  const [generatedDescription, setGeneratedDescription] = useState("");
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateCategory, setTemplateCategory] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    property_type: initialData.property_type || "apartament",
    rooms: initialData.rooms || "2",
    useful_area: initialData.surface || "",
    total_area: "",
    compartment: "decomandat",
    floor: "",
    total_floors: "",
    year_built: "",
    zone: initialData.zone || "",
    city: initialData.city || "București",
    condition: "stare_buna",
    floor_type: "parchet_laminat",
    walls: "lavabil",
    windows: "termopan",
    electrical: "buna",
    plumbing: "buna",
    heating: "centrala_proprie",
    has_balcony: false,
    has_parking: false,
    has_storage: false,
    has_ac: false,
    has_furnished_kitchen: false,
    is_furnished: false,
    building_type: "bloc_reabilitat",
    building_structure: "beton",
    has_elevator: false,
    near_metro: false,
    metro_distance: "",
    near_park: false,
    park_name: "",
    near_school: false,
    near_mall: false,
    mall_name: "",
    main_advantage: "",
    price: initialData.price || "",
    currency: initialData.currency || "EUR",
    ...initialData
  });

  // Load templates and categories on mount
  useEffect(() => {
    loadTemplates();
    loadCategories();
  }, []);

  const loadTemplates = async (category = "") => {
    setLoadingTemplates(true);
    try {
      const url = category ? `${API}/ai/templates?category=${encodeURIComponent(category)}` : `${API}/ai/templates`;
      const response = await axios.get(url);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/ai/templates/categories/list`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    loadTemplates(category);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    if (!formData.property_type || !formData.rooms || !formData.zone) {
      toast.error("Completează cel puțin: tip proprietate, camere și zonă");
      return;
    }

    setGenerating(true);
    
    try {
      const response = await axios.post(`${API}/ai/generate-listing`, formData);
      
      setGeneratedTitle(response.data.title);
      setGeneratedDescription(response.data.description);
      
      if (onTitleGenerated) onTitleGenerated(response.data.title);
      if (onDescriptionGenerated) onDescriptionGenerated(response.data.description);
      
      toast.success("Anunț generat cu succes!");
      
    } catch (error) {
      console.error("Error generating listing:", error);
      toast.error(error.response?.data?.detail || "Eroare la generarea anunțului");
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copiat în clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Nu s-a putut copia");
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Introduceți un nume pentru template");
      return;
    }

    // Determine the category to use
    const categoryToUse = newCategory.trim() || (templateCategory === "__none__" ? "" : templateCategory);

    setSavingTemplate(true);
    try {
      const templateData = {
        name: templateName,
        category: categoryToUse,
        property_type: formData.property_type,
        rooms: formData.rooms,
        compartment: formData.compartment,
        condition: formData.condition,
        floor_type: formData.floor_type,
        heating: formData.heating,
        has_balcony: formData.has_balcony,
        has_parking: formData.has_parking,
        has_storage: formData.has_storage,
        has_ac: formData.has_ac,
        has_furnished_kitchen: formData.has_furnished_kitchen,
        is_furnished: formData.is_furnished,
        has_elevator: formData.has_elevator,
        near_metro: formData.near_metro,
        near_park: formData.near_park,
        near_school: formData.near_school,
        near_mall: formData.near_mall,
        title_template: generatedTitle,
        description_template: generatedDescription
      };

      await axios.post(`${API}/ai/templates`, templateData);
      toast.success("Template salvat cu succes!");
      setShowSaveDialog(false);
      setTemplateName("");
      setTemplateCategory("");
      setNewCategory("");
      loadTemplates();
      loadCategories();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Eroare la salvarea template-ului");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleLoadTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      property_type: template.property_type || prev.property_type,
      rooms: template.rooms || prev.rooms,
      compartment: template.compartment || prev.compartment,
      condition: template.condition || prev.condition,
      floor_type: template.floor_type || prev.floor_type,
      heating: template.heating || prev.heating,
      has_balcony: template.has_balcony || false,
      has_parking: template.has_parking || false,
      has_storage: template.has_storage || false,
      has_ac: template.has_ac || false,
      has_furnished_kitchen: template.has_furnished_kitchen || false,
      is_furnished: template.is_furnished || false,
      has_elevator: template.has_elevator || false,
      near_metro: template.near_metro || false,
      near_park: template.near_park || false,
      near_school: template.near_school || false,
      near_mall: template.near_mall || false,
    }));
    
    if (template.title_template) {
      setGeneratedTitle(template.title_template);
    }
    if (template.description_template) {
      setGeneratedDescription(template.description_template);
    }
    
    setShowTemplatesDialog(false);
    toast.success(`Template "${template.name}" încărcat`);
  };

  const handleDeleteTemplate = async (templateId, e) => {
    e.stopPropagation();
    if (!window.confirm("Sigur doriți să ștergeți acest template?")) return;
    
    try {
      await axios.delete(`${API}/ai/templates/${templateId}`);
      toast.success("Template șters");
      loadTemplates();
    } catch (error) {
      toast.error("Eroare la ștergerea template-ului");
    }
  };

  const getPropertyTypeLabel = (value) => {
    return PROPERTY_TYPES.find(t => t.value === value)?.label || value;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-stone-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900">Generator Anunț AI</h3>
            <p className="text-sm text-stone-500">Completează detaliile și generează anunțul automat</p>
          </div>
        </div>
        
        {/* Templates Button */}
        <Dialog open={showTemplatesDialog} onOpenChange={setShowTemplatesDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid="open-templates-btn">
              <FolderOpen className="w-4 h-4" />
              Template-uri
              {templates.length > 0 && (
                <Badge variant="secondary" className="ml-1">{templates.length}</Badge>
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Template-uri salvate</DialogTitle>
            </DialogHeader>
            
            {/* Category Filter */}
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2 pb-3 border-b border-stone-100">
                <Button
                  variant={selectedCategory === "" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleCategoryFilter("")}
                  className="text-xs h-7"
                >
                  Toate
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleCategoryFilter(cat)}
                    className="text-xs h-7"
                    data-testid={`category-filter-${cat}`}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            )}
            
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nu aveți template-uri {selectedCategory ? `în categoria "${selectedCategory}"` : "salvate"}</p>
                  <p className="text-sm">Generați un anunț și salvați-l ca template</p>
                </div>
              ) : (
                templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleLoadTemplate(template)}
                    className="p-4 border border-stone-200 rounded-lg hover:border-violet-300 hover:bg-violet-50/50 cursor-pointer transition-all group"
                    data-testid={`template-item-${template.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-stone-900 truncate">{template.name}</h4>
                          {template.category && (
                            <Badge variant="secondary" className="text-xs bg-violet-100 text-violet-700">
                              {template.category}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-stone-500">
                          <Badge variant="outline" className="text-xs">
                            {getPropertyTypeLabel(template.property_type)}
                          </Badge>
                          {template.rooms && (
                            <span>{template.rooms} camere</span>
                          )}
                        </div>
                        {template.title_template && (
                          <p className="mt-2 text-sm text-stone-600 line-clamp-1">
                            {template.title_template}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDeleteTemplate(template.id, e)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Basic Info - Always Visible */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <Label>Tip proprietate *</Label>
          <Select value={formData.property_type} onValueChange={(v) => updateField("property_type", v)}>
            <SelectTrigger data-testid="property-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Camere *</Label>
          <Select value={formData.rooms} onValueChange={(v) => updateField("rooms", v)}>
            <SelectTrigger data-testid="rooms-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1,2,3,4,5,6].map(n => (
                <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? "cameră" : "camere"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Suprafață utilă (mp) *</Label>
          <Input 
            type="number" 
            placeholder="65"
            value={formData.useful_area}
            onChange={(e) => updateField("useful_area", e.target.value)}
            data-testid="useful-area-input"
          />
        </div>
        
        <div>
          <Label>Compartimentare</Label>
          <Select value={formData.compartment} onValueChange={(v) => updateField("compartment", v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMPARTMENTS.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Location */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <Label>Zonă / Cartier *</Label>
          <Input 
            placeholder="ex: Bucur Obor, Floreasca"
            value={formData.zone}
            onChange={(e) => updateField("zone", e.target.value)}
            data-testid="zone-input"
          />
        </div>
        <div>
          <Label>Oraș</Label>
          <Input 
            placeholder="București"
            value={formData.city}
            onChange={(e) => updateField("city", e.target.value)}
          />
        </div>
        <div>
          <Label>Preț</Label>
          <div className="flex gap-2">
            <Input 
              type="number"
              placeholder="85000"
              value={formData.price}
              onChange={(e) => updateField("price", e.target.value)}
            />
            <Select value={formData.currency} onValueChange={(v) => updateField("currency", v)}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="RON">RON</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Advanced Toggle */}
      <Button
        type="button"
        variant="ghost"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="w-full justify-between text-stone-600"
      >
        <span>Detalii avansate (finisaje, instalații, zonă)</span>
        {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </Button>

      {/* Advanced Options */}
      {showAdvanced && (
        <div className="space-y-6 pt-4 border-t border-stone-100">
          {/* Property Details */}
          <div>
            <h4 className="font-medium text-stone-800 mb-3">Detalii proprietate</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Etaj</Label>
                <Input 
                  placeholder="ex: 3"
                  value={formData.floor}
                  onChange={(e) => updateField("floor", e.target.value)}
                />
              </div>
              <div>
                <Label>Etaje clădire</Label>
                <Input 
                  placeholder="ex: 10"
                  value={formData.total_floors}
                  onChange={(e) => updateField("total_floors", e.target.value)}
                />
              </div>
              <div>
                <Label>An construcție</Label>
                <Input 
                  placeholder="ex: 2010"
                  value={formData.year_built}
                  onChange={(e) => updateField("year_built", e.target.value)}
                />
              </div>
              <div>
                <Label>Stare imobil</Label>
                <Select value={formData.condition} onValueChange={(v) => updateField("condition", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_CONDITIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Finishes & Installations */}
          <div>
            <h4 className="font-medium text-stone-800 mb-3">Finisaje și instalații</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Pardoseală</Label>
                <Select value={formData.floor_type} onValueChange={(v) => updateField("floor_type", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FLOOR_TYPES.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Încălzire</Label>
                <Select value={formData.heating} onValueChange={(v) => updateField("heating", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HEATING_TYPES.map(h => (
                      <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div>
            <h4 className="font-medium text-stone-800 mb-3">Dotări</h4>
            <div className="flex flex-wrap gap-4">
              {[
                { key: "has_balcony", label: "Balcon / Terasă" },
                { key: "has_parking", label: "Loc parcare" },
                { key: "has_storage", label: "Boxă" },
                { key: "has_ac", label: "Aer condiționat" },
                { key: "has_furnished_kitchen", label: "Bucătărie mobilată" },
                { key: "is_furnished", label: "Mobilat complet" },
                { key: "has_elevator", label: "Lift" },
              ].map(item => (
                <label key={item.key} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={formData[item.key]}
                    onCheckedChange={(checked) => updateField(item.key, checked)}
                  />
                  <span className="text-sm text-stone-700">{item.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Area Details */}
          <div>
            <h4 className="font-medium text-stone-800 mb-3">Detalii zonă</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={formData.near_metro}
                    onCheckedChange={(checked) => updateField("near_metro", checked)}
                  />
                  <span className="text-sm text-stone-700">Aproape de metrou</span>
                </label>
                {formData.near_metro && (
                  <Input 
                    placeholder="Distanță (ex: 2 min)"
                    value={formData.metro_distance}
                    onChange={(e) => updateField("metro_distance", e.target.value)}
                    className="w-40"
                  />
                )}
              </div>
              
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={formData.near_park}
                    onCheckedChange={(checked) => updateField("near_park", checked)}
                  />
                  <span className="text-sm text-stone-700">Aproape de parc</span>
                </label>
                {formData.near_park && (
                  <Input 
                    placeholder="Nume parc"
                    value={formData.park_name}
                    onChange={(e) => updateField("park_name", e.target.value)}
                    className="w-40"
                  />
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={formData.near_mall}
                    onCheckedChange={(checked) => updateField("near_mall", checked)}
                  />
                  <span className="text-sm text-stone-700">Aproape de mall/magazine</span>
                </label>
                {formData.near_mall && (
                  <Input 
                    placeholder="Nume mall"
                    value={formData.mall_name}
                    onChange={(e) => updateField("mall_name", e.target.value)}
                    className="w-40"
                  />
                )}
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox 
                  checked={formData.near_school}
                  onCheckedChange={(checked) => updateField("near_school", checked)}
                />
                <span className="text-sm text-stone-700">Aproape de școli/grădinițe</span>
              </label>
            </div>
          </div>

          {/* Main Advantage */}
          <div>
            <Label>Avantaj principal (pentru titlu)</Label>
            <Input 
              placeholder="ex: vedere panoramică, renovat lux, zonă liniștită"
              value={formData.main_advantage}
              onChange={(e) => updateField("main_advantage", e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Generate Button */}
      <Button
        onClick={handleGenerate}
        disabled={generating}
        className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
        data-testid="generate-ai-btn"
      >
        {generating ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Se generează cu AI...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5 mr-2" />
            Generează anunț cu AI
          </>
        )}
      </Button>

      {/* Generated Results */}
      {generatedTitle && (
        <div className="space-y-4 pt-6 border-t border-stone-200">
          {/* Save Template Button */}
          <div className="flex justify-end">
            <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" data-testid="save-template-btn">
                  <Save className="w-4 h-4" />
                  Salvează ca template
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Salvează template</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Nume template *</Label>
                    <Input
                      placeholder="ex: Apartament modern București"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      data-testid="template-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Categorie</Label>
                    {categories.length > 0 ? (
                      <Select value={templateCategory} onValueChange={setTemplateCategory}>
                        <SelectTrigger data-testid="category-select">
                          <SelectValue placeholder="Selectează o categorie existentă" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Fără categorie</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                    <div className="mt-2">
                      <Input
                        placeholder="Sau creează categorie nouă (ex: Apartamente București)"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        data-testid="new-category-input"
                      />
                      <p className="text-xs text-stone-400 mt-1">
                        Lasă gol pentru a folosi categoria selectată mai sus
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-sm text-stone-500 bg-stone-50 p-3 rounded-lg">
                    <p className="font-medium text-stone-700">Template-ul va salva:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Setările formularului (tip, camere, dotări)</li>
                      <li>Titlul și descrierea generate</li>
                      <li>Categoria pentru organizare ușoară</li>
                    </ul>
                  </div>
                  <Button
                    onClick={handleSaveTemplate}
                    disabled={savingTemplate || !templateName.trim()}
                    className="w-full"
                    data-testid="confirm-save-template-btn"
                  >
                    {savingTemplate ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Se salvează...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Salvează template
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Generated Title */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-emerald-700">Titlu generat</Label>
              <Badge variant="secondary" className="text-xs">
                {generatedTitle.length}/80 caractere
              </Badge>
            </div>
            <div className="relative">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-stone-900 font-medium" data-testid="generated-title">
                {generatedTitle}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copyToClipboard(generatedTitle)}
                className="absolute right-2 top-1/2 -translate-y-1/2"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Generated Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-emerald-700">Descriere generată</Label>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(generatedDescription)}
              >
                <Copy className="w-4 h-4 mr-1" />
                Copiază tot
              </Button>
            </div>
            <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg max-h-[400px] overflow-y-auto" data-testid="generated-description">
              <pre className="whitespace-pre-wrap text-sm text-stone-700 font-sans">
                {generatedDescription}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIListingGenerator;
